import PropTypes from 'prop-types'
import { Fragment, useState } from 'react'

import agent from 'agent'
import ConfirmationModal from 'components/common/ConfirmationModal'
import TextButton from 'components/common/TextButton'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { messageFromResponseError } from 'utils/strings'

const ArchiveConfirmationModal = ({ open, onConfirm, onCancel }) => {
  return (
    <ConfirmationModal
      confirmButtonType="destructive"
      confirmText="Archive Scenario"
      message="Are you sure you want to archive this scenario?"
      open={open}
      title="Archive this Scenario?"
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  )
}

ArchiveConfirmationModal.propTypes = {
  open: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func
}

const ArchiveScenarioTextButton = ({ scenario, doFetch, ...rest }) => {
  const { currentTeamId } = useAuth()

  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <Fragment>
      <TextButton
        data-test-archive-button
        destructive
        data-track-action="archive-scenario"
        data-track-on="click"
        onClick={() => setIsModalOpen(true)}
        {...rest}
      >
        Archive
      </TextButton>
      <ArchiveConfirmationModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={async () => {
          try {
            await agent.post(
              `/scenarios/${scenario.guid}/archive?teamId=${currentTeamId}`,
              null,
              {
                headers: {
                  'Content-Type': 'text/plain'
                }
              }
            )
          } catch (err) {
            return toast({
              type: 'error',
              text: messageFromResponseError(
                err,
                'There was an issue archiving this scenario. Please try again later.'
              )
            })
          }

          toast({
            type: 'success',
            text: 'Scenario archived'
          })

          doFetch()

          setIsModalOpen(false)
        }}
      />
    </Fragment>
  )
}

ArchiveScenarioTextButton.propTypes = {
  doFetch: PropTypes.func.isRequired,
  scenario: PropTypes.object.isRequired
}

function UnarchiveConfirmationModal({ open, onConfirm, onCancel }) {
  return (
    <ConfirmationModal
      confirmButtonType="primary"
      confirmText="Unarchive Scenario"
      message="Are you sure you want to unarchive this scenario?"
      open={open}
      title="Unarchive this Scenario?"
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  )
}

UnarchiveConfirmationModal.propTypes = {
  open: PropTypes.bool,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func
}

const UnarchiveScenarioTextButton = ({ scenario, doFetch, ...rest }) => {
  const { currentTeamId } = useAuth()

  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <Fragment>
      <TextButton
        data-test-unarchive-button
        secondary
        data-track-action="unarchive-scenario"
        data-track-on="click"
        onClick={() => setIsModalOpen(true)}
        {...rest}
      >
        Unarchive
      </TextButton>
      <UnarchiveConfirmationModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={async () => {
          try {
            await agent.post(
              `/scenarios/${scenario.guid}/restore?teamId=${currentTeamId}`,
              null,
              {
                headers: {
                  'Content-Type': 'text/plain'
                }
              }
            )
          } catch (err) {
            return toast({
              type: 'error',
              text: messageFromResponseError(
                err,
                'There was an issue restoring this scenario. Please try again later.'
              )
            })
          }

          toast({
            type: 'success',
            text: 'Scenario restored'
          })

          doFetch()

          setIsModalOpen(false)
        }}
      />
    </Fragment>
  )
}

UnarchiveScenarioTextButton.propTypes = {
  doFetch: PropTypes.func.isRequired,
  scenario: PropTypes.object.isRequired
}

const UpdateScenarioArchiveStatusTextButton = ({
  scenario,
  doFetch,
  ...rest
}) => {
  const { state } = scenario

  if (state === 'ARCHIVED') {
    return (
      <UnarchiveScenarioTextButton
        doFetch={doFetch}
        scenario={scenario}
        {...rest}
      />
    )
  }

  return (
    <ArchiveScenarioTextButton
      doFetch={doFetch}
      scenario={scenario}
      {...rest}
    />
  )
}

UpdateScenarioArchiveStatusTextButton.propTypes = {
  doFetch: PropTypes.func.isRequired,
  scenario: PropTypes.object.isRequired
}

export default UpdateScenarioArchiveStatusTextButton
